import { useEffect, useMemo, useState } from "react";
import { ApiError, listDeliveries, mailboxLabel, type DeliveryBoard, type Direction, type Mailbox, type MailboxInfo } from "../api";
import { CaseList } from "./deliveries/CaseList";
import { casesOf } from "./deliveries/model";
import { Health, Verdict } from "./deliveries/Status";

// The Deliveries board: did the bank receive, and accept, what we sent? Each
// delivery uploaded through a mailbox beside the bank's receipts for it — T, the
// transmission receipt (the file arrived and is well formed), and V, the
// validation receipt (its content can be processed) — gathered into cases
// (deliveries/model.ts), problems first. On top, a verdict to read from across
// the room, and anything that makes the board itself untrustworthy.
//
// It is made to be left open on a monitor: it refreshes itself every minute,
// says when it last did, and says so plainly when it could not — a board that
// silently stopped would be worse than none.

const REFRESH_MS = 60_000;
/** A refresh still out after this is given up and shown as failed, so it can never hang the board. */
const TIMEOUT_MS = 30_000;
/** Past this without a successful refresh, the board says it is out of date. */
const STALE_MS = 3 * REFRESH_MS;
const WINDOWS = [7, 30, 60, 120, 365] as const;

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));
const clock = new Intl.DateTimeFormat("da-DK", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

export function DeliveriesBoard({
  token,
  mailboxes,
  mailbox,
  onMailbox,
  onOpenFile,
  onUnauthorized,
}: {
  token: string;
  mailboxes: MailboxInfo[];
  mailbox: Mailbox;
  onMailbox: (mailbox: Mailbox) => void;
  onOpenFile: (mailbox: Mailbox, direction: Direction, name: string) => void;
  /** A refresh was refused for want of a valid sign-in: renew it now rather than at the next minute. */
  onUnauthorized: () => void;
}): JSX.Element {
  const [days, setDays] = useState<number>(60);
  const [board, setBoard] = useState<DeliveryBoard | undefined>();
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>();
  const [failure, setFailure] = useState<{ at: Date; error: string } | undefined>();
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  // Ticks so "out of date" shows up even when nothing else happens — a laptop
  // that slept, a tab the browser stopped running timers in.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let inFlight: AbortController | undefined;
    const load = async (): Promise<void> => {
      setLoading(true);
      const abort = new AbortController();
      inFlight = abort;
      const timeout = window.setTimeout(() => abort.abort(), TIMEOUT_MS);
      try {
        const next = await listDeliveries(token, mailbox, days, abort.signal);
        if (cancelled) return;
        setBoard(next);
        setUpdatedAt(new Date());
        setFailure(undefined);
      } catch (err) {
        if (cancelled) return;
        setFailure({ at: new Date(), error: abort.signal.aborted ? `no answer within ${TIMEOUT_MS / 1000} seconds` : message(err) });
        if (err instanceof ApiError && err.status === 401) onUnauthorized();
      } finally {
        window.clearTimeout(timeout);
        if (!cancelled) {
          setLoading(false);
          setNow(Date.now());
          timer = window.setTimeout(() => void load(), REFRESH_MS);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      inFlight?.abort();
    };
  }, [token, mailbox, days, nonce, onUnauthorized]);

  const shown = board?.mailbox === mailbox ? board : undefined;
  const cases = useMemo(() => (shown ? casesOf(shown) : []), [shown]);
  const info = mailboxes.find((m) => m.id === mailbox) ?? { id: mailbox, subsystem: mailbox.toUpperCase() };
  const stale = updatedAt !== undefined && now - updatedAt.getTime() > STALE_MS;
  const open = (direction: Direction, name: string): void => onOpenFile(mailbox, direction, name);
  const freshness = { loading, updatedAt, failure, stale };

  return (
    <main className="deliveries">
      <div className="deliveries-head">
        <h2>Deliveries</h2>
        <div className="segmented" role="group" aria-label="Which login's deliveries">
          {mailboxes.map((m) => (
            <button key={m.id} type="button" aria-pressed={m.id === mailbox} onClick={() => onMailbox(m.id)}>
              {mailboxLabel(m)}
            </button>
          ))}
        </div>
        <label className="deliveries-window">
          <span className="label">Uploaded in the last</span>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {WINDOWS.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        <span className="deliveries-updated" aria-live="polite">
          {loading ? "Updating…" : updatedAt ? `Updated ${clock.format(updatedAt)}` : ""}
        </span>
        <button type="button" className="linkish" onClick={() => setNonce((n) => n + 1)}>
          Refresh now
        </button>
      </div>

      {shown ? (
        <>
          <Health board={shown} info={info} now={now} freshness={freshness} />
          <Verdict board={shown} cases={cases} now={now} />
          <CaseList cases={cases} now={now} onOpen={open} />
        </>
      ) : failure ? (
        <p className="board-alarm" data-tone="bad" role="alert">
          <span className="board-alarm-mark">!</span>
          <span>
            <b>Could not load the board: {failure.error.replace(/\.$/, "")}.</b> Retrying every minute.
          </span>
        </p>
      ) : (
        <p className="admin-status">Loading…</p>
      )}
    </main>
  );
}
